import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { enrollmentAPI, studentAPI, batchAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/dateUtils';
import { getCurrentDateIST } from '../../utils/dateUtils';
import ConfirmationModal from '../ConfirmationModal';

const EnrollmentForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    student_id: '',
    batch_id: '',
    enrollment_date: getCurrentDateIST(),
    enrollment_fee_paid: false,
    enrollment_fee_amount: 0,
    remarks: ''
  });

  const [showFeeWarningModal, setShowFeeWarningModal] = useState(false);
  const [pendingEnrollmentData, setPendingEnrollmentData] = useState(null);

  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentAPI.getAll(0, 100, true).then(res => res.data),
  });

  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => batchAPI.getAll(0, 100, true).then(res => res.data),
  });

  const { data: enrollments, isLoading: enrollmentsLoading } = useQuery({
    queryKey: ['enrollments'],
    queryFn: () => enrollmentAPI.getAll(0, 100, true).then(res => res.data),
  });

  const createMutation = useMutation({
    mutationFn: (data) => enrollmentAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['enrollments']);
      navigate('/enrollments');
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If enrollment fee is required but not paid, show warning
    const selectedBatch = getSelectedBatch();
    if (selectedBatch?.fee_amount && selectedBatch.fee_amount > 0 && !formData.enrollment_fee_paid) {
      setPendingEnrollmentData(formData);
      setShowFeeWarningModal(true);
      return;
    }
    
    createMutation.mutate(formData);
  };

  const confirmEnrollmentWithPendingFee = () => {
    if (pendingEnrollmentData) {
      createMutation.mutate(pendingEnrollmentData);
      setShowFeeWarningModal(false);
      setPendingEnrollmentData(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getSelectedBatch = () => {
    return batches?.find(batch => (batch._id || batch.id) === formData.batch_id);
  };

  // Filter out students who are already enrolled
  const getAvailableStudents = () => {
    if (!students || !enrollments) return students;
    
    const enrolledStudentIds = enrollments.map(enrollment => 
      enrollment.student_id || enrollment.student?.id
    );
    
    return students.filter(student => 
      !enrolledStudentIds.includes(student._id || student.id)
    );
  };

  if (studentsLoading || batchesLoading || enrollmentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/enrollments')}
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Enrollments
        </button>
        <h1 className="text-3xl font-bold text-white">New Enrollment</h1>
        <p className="text-gray-300 mt-2">Enroll a student in a batch (one enrollment per student)</p>
      </div>

      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="student_id" className="block text-sm font-medium text-gray-300 mb-2">
              Student *
            </label>
            <select
              id="student_id"
              name="student_id"
              value={formData.student_id}
              onChange={handleChange}
              required
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 bg-gray-700 text-white"
            >
              <option value="">Select a student</option>
              {getAvailableStudents()?.map((student) => (
                <option key={student._id || student.id} value={student._id || student.id}>
                  {student.first_name} {student.last_name} ({student.student_id})
                </option>
              ))}
            </select>
            {getAvailableStudents()?.length === 0 && (
              <p className="text-sm text-red-400 mt-1">All students are already enrolled. No available students for enrollment.</p>
            )}
          </div>

          <div>
            <label htmlFor="batch_id" className="block text-sm font-medium text-gray-300 mb-2">
              Batch *
            </label>
            <select
              id="batch_id"
              name="batch_id"
              value={formData.batch_id}
              onChange={handleChange}
              required
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 bg-gray-700 text-white"
            >
              <option value="">Select a batch</option>
              {batches?.map((batch) => (
                <option key={batch._id || batch.id} value={batch._id || batch.id}>
                  {batch.batch_name} - {batch.subject} {batch.fee_amount ? `(${formatIndianCurrency(batch.fee_amount)})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Fee Information */}
          {formData.batch_id && getSelectedBatch()?.fee_amount && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
              <div className="space-y-4">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-yellow-400 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-300">Enrollment Fee</h3>
                    <p className="text-lg font-semibold text-yellow-200">{formatIndianCurrency(getSelectedBatch().fee_amount)}</p>
                    <p className="text-sm text-yellow-400 mt-1">
                      This fee must be collected during enrollment.
                    </p>
                  </div>
                </div>
                
                {/* Payment Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.enrollment_fee_paid}
                      onChange={(e) => setFormData(prev => ({ ...prev, enrollment_fee_paid: e.target.checked }))}
                      className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600 rounded bg-gray-700"
                    />
                    <span className="ml-2 text-sm font-medium text-yellow-300">
                      Enrollment fee has been collected
                    </span>
                  </label>
                </div>
                
                {/* Payment Details (shown when fee is marked as paid) */}
                {formData.enrollment_fee_paid && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-yellow-700">
                    <div>
                      <label htmlFor="payment_method" className="block text-sm font-medium text-yellow-300 mb-1">
                        Payment Method
                      </label>
                      <select
                        id="payment_method"
                        value={formData.payment_method}
                        onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                        className="block w-full px-3 py-2 border border-yellow-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm bg-gray-700 text-white"
                      >
                        <option value="cash">Cash</option>
                        <option value="online">Online</option>
                        <option value="cheque">Cheque</option>
                        <option value="card">Card</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="payment_date" className="block text-sm font-medium text-yellow-300 mb-1">
                        Payment Date
                      </label>
                      <input
                        type="date"
                        id="payment_date"
                        value={formData.payment_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                        className="block w-full px-3 py-2 border border-yellow-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm bg-gray-700 text-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {createMutation.error && (
            <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
              <p className="text-sm text-red-300">
                {createMutation.error?.response?.data?.detail || 'An error occurred. Please try again.'}
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/enrollments')}
              className="px-4 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:ring-offset-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:ring-offset-gray-800 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Enrollment & Collect Fee'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmationModal
        isOpen={showFeeWarningModal}
        onClose={() => setShowFeeWarningModal(false)}
        onConfirm={confirmEnrollmentWithPendingFee}
        title="Pending Enrollment Fee"
        message={`Enrollment fee of ${formatIndianCurrency(getSelectedBatch()?.fee_amount)} is required but not marked as paid. Do you want to proceed with pending payment?`}
        confirmText="Yes, Proceed"
        cancelText="No, Cancel"
      />
    </div>
  );
};

export default EnrollmentForm; 